import { useUser } from "@clerk/clerk-expo";
import type { FeedbackModalIntegration } from "@sentry/core";
import { getFeedback } from "@sentry/react";
import {
  CircleUserRound,
  LogOut,
  Megaphone,
  Settings as SettingsIcon,
} from "@tamagui/lucide-icons";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { Circle, Popover, XStack, YStack, getTokenValue } from "tamagui";

import { useGetUserProfile } from "@/api/queries/userProfile";
import { Button } from "@/components/Button";
import { Text } from "@/components/Typography";
import useLogout from "@/hooks/useLogout";

export const getDisplayName = (
  preferredName?: string | null,
  firstName?: string | null,
  lastName?: string | null,
): string => {
  if (preferredName) return preferredName;
  if (firstName) return lastName ? `${firstName} ${lastName}` : firstName;
  return "";
};

export default function SettingsMenu({
  collapsed = false,
}: {
  collapsed?: boolean;
}) {
  const router = useRouter();
  const logout = useLogout();
  const { data: userProfile } = useGetUserProfile();
  const [feedbackForm, setFeedbackForm] =
    useState<ReturnType<FeedbackModalIntegration["createDialog"]>>();
  const [userPhoneNumber, setUserPhoneNumber] = useState<string>("");
  const { user, isLoaded: isUserLoaded } = useUser();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const initFeedback = async () => {
      const feedback = getFeedback();
      if (feedback) {
        const form = await feedback.createForm();
        form.appendToDom();
        setFeedbackForm(form);
        return form?.removeFromDom;
      }
    };
    const cleanup = initFeedback();
    return () => {
      cleanup?.then((cleanupFn) => cleanupFn?.());
    };
  }, []);

  useEffect(() => {
    if (isUserLoaded) {
      setUserPhoneNumber(user?.primaryPhoneNumber?.phoneNumber || "");
    }
  }, [isUserLoaded, user]);

  const profileName = getDisplayName(
    userProfile?.preferredName,
    userProfile?.firstName,
    userProfile?.lastName,
  );
  const displayName = profileName || userPhoneNumber;

  return (
    <Popover
      allowFlip
      placement="top-start"
      open={isOpen}
      onOpenChange={setIsOpen}
      offset={{ mainAxis: 6 }}
    >
      <Popover.Trigger asChild>
        <Button.Wrapper
          gap="$100"
          alignItems="center"
          alignSelf={collapsed ? "flex-start" : undefined}
          borderRadius="$150"
          padding="$100"
          backgroundColor={isOpen ? "$navSelected" : "transparent"}
          hoverStyle={{
            backgroundColor: "$navHover",
          }}
          aria-label="Settings menu"
        >
          {profileName ? (
            <Circle size="$250" backgroundColor="$fillPatient">
              <Text color="$textPatient">{profileName[0]}</Text>
            </Circle>
          ) : (
            <CircleUserRound size="$250" color="$textSecondary" />
          )}
          {!collapsed && <Text color="$textSecondary">{displayName}</Text>}
        </Button.Wrapper>
      </Popover.Trigger>
      <Popover.Content
        enterStyle={{ y: 10, opacity: 0 }}
        exitStyle={{ y: 10, opacity: 0 }}
        transition="standard"
        padding="$75"
        backgroundColor="$fillModal"
        borderColor="$strokePopover"
        borderWidth="$strokeWidth"
        minWidth={
          getTokenValue("$sidebarExpandedWidth") - getTokenValue("$100")
        }
        alignItems="stretch"
        borderRadius="$150"
      >
        <YStack gap="$100" role="menu" width="100%">
          {Boolean(profileName) && Boolean(userPhoneNumber) && (
            <XStack gap="$100" alignItems="center" padding="$100">
              <CircleUserRound size="$250" color="$textDisabled" />
              <Text color="$textDisabled" size="$sm">
                {userPhoneNumber}
              </Text>
            </XStack>
          )}
          <Popover.Close asChild>
            <Button
              mode="subMenu"
              role="menuitem"
              icon={<SettingsIcon size="$250" />}
              onPress={() => router.setParams({ settings: "profile" })}
            >
              Settings
            </Button>
          </Popover.Close>
          <Popover.Close asChild>
            <Button
              mode="subMenu"
              role="menuitem"
              icon={<Megaphone size="$250" />}
              onPress={() => feedbackForm?.open()}
            >
              Feedback
            </Button>
          </Popover.Close>
          <Popover.Close asChild>
            <Button
              onPress={logout}
              mode="subMenu"
              role="menuitem"
              icon={<LogOut size="$250" />}
            >
              Log out
            </Button>
          </Popover.Close>
        </YStack>
      </Popover.Content>
    </Popover>
  );
}
